import axios from 'axios';
import { supabase } from '../lib/supabase';
import { getUserTimezone } from '../utils/dateUtils';

// Create axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add JWT token, business context, and timezone to all requests
// Supports both Flask tokens (localStorage) and Supabase tokens
api.interceptors.request.use(
  async (config) => {
    // First, check for Supabase session token
    const { data: { session } } = await supabase.auth.getSession();

    if (session?.access_token) {
      // Use Supabase token if available
      config.headers.Authorization = `Bearer ${session.access_token}`;
    } else {
      // Fall back to Flask token from localStorage
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Add business context header if available (for both advisors and business owners)
    const currentBusinessId = localStorage.getItem('currentBusinessId');

    if (currentBusinessId) {
      config.headers['X-Business-Id'] = currentBusinessId;
    }

    // Add user timezone header for timezone-aware date conversion on backend
    const userTimezone = getUserTimezone();
    if (userTimezone) {
      config.headers['X-User-Timezone'] = userTimezone;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear both auth systems
      localStorage.removeItem('token');

      // Sign out from Supabase if session exists
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await supabase.auth.signOut();
      }

      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Assessment API Functions

export const startAssessment = async () => {
  try {
    const response = await api.post('/assessment/start');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error starting assessment:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to start assessment'
    };
  }
};

export const getAssessment = async () => {
  try {
    const response = await api.get('/assessment/current');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting assessment:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get assessment'
    };
  }
};

export const saveAssessmentResponse = async (responseData) => {
  try {
    const response = await api.post('/assessment/response', responseData);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error saving response:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to save response'
    };
  }
};

export const getAssessmentTasks = async () => {
  try {
    const response = await api.get('/assessment/tasks');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting tasks:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get tasks'
    };
  }
};

export const getAssessmentQuestions = async () => {
  try {
    // Include business, family enterprise, and personal assessment questions
    const response = await api.get('/assessment/questions?assessment_types=business,family enterprise,personal');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting assessment questions:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get assessment questions'
    };
  }
};

export const updateTaskStatus = async (taskId, status) => {
  try {
    const response = await api.put(`/assessment/task/${taskId}/status`, { status });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error updating task:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to update task'
    };
  }
};

// Task Manager API Functions
export const getTasks = async (params = {}) => {
  try {
    const response = await api.get('/tasks', { params });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting tasks:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get tasks'
    };
  }
};

export const getTaskStats = async () => {
  try {
    const response = await api.get('/tasks/stats');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting task stats:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get task stats'
    };
  }
};

export const createTask = async (taskData) => {
  try {
    const response = await api.post('/tasks', taskData);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error creating task:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to create task'
    };
  }
};

export const updateTask = async (taskId, taskData) => {
  try {
    const response = await api.put(`/tasks/${taskId}`, taskData);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error updating task:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to update task'
    };
  }
};

export const deleteTask = async (taskId) => {
  try {
    const response = await api.delete(`/tasks/${taskId}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error deleting task:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to delete task'
    };
  }
};

export const retakeAssessment = async () => {
  try {
    const response = await api.post('/assessment/retake');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error retaking assessment:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to retake assessment'
    };
  }
};

export const getAssessmentHistory = async () => {
  try {
    const response = await api.get('/assessment/history');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting assessment history:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get assessment history'
    };
  }
};

export const getSpecificAssessment = async (assessmentId) => {
  try {
    const response = await api.get(`/assessment/${assessmentId}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting specific assessment:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get assessment'
    };
  }
};

export const downloadAssessmentPDF = async (assessmentId) => {
  try {
    const response = await api.get(`/assessment/${assessmentId}/pdf`, {
      responseType: 'blob'
    });

    // Create blob from response
    const blob = new Blob([response.data], { type: 'application/pdf' });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `assessment_report_${assessmentId}_${new Date().toISOString().split('T')[0]}.pdf`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Error downloading PDF:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to download PDF'
    };
  }
};

export const getAssessmentSummary = async (assessmentId) => {
  try {
    const response = await api.get(`/assessment/${assessmentId}/summary`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting assessment summary:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get assessment summary'
    };
  }
};

// Personal Assessment API Functions

export const startPersonalAssessment = async () => {
  try {
    const response = await api.post('/personal-assessment/start');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error starting personal assessment:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to start personal assessment'
    };
  }
};

export const getPersonalAssessment = async () => {
  try {
    const response = await api.get('/personal-assessment/current');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting personal assessment:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get personal assessment'
    };
  }
};

export const savePersonalAssessmentResponse = async (responseData) => {
  try {
    const response = await api.post('/personal-assessment/response', responseData);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error saving personal response:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to save response'
    };
  }
};

export const getPersonalAssessmentQuestions = async () => {
  try {
    const response = await api.get('/personal-assessment/questions');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting personal assessment questions:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get questions'
    };
  }
};

export const retakePersonalAssessment = async () => {
  try {
    const response = await api.post('/personal-assessment/retake');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error retaking personal assessment:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to retake personal assessment'
    };
  }
};

export const getPersonalAssessmentSummary = async (assessmentId) => {
  try {
    const response = await api.get(`/personal-assessment/${assessmentId}/summary`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting personal assessment summary:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get personal assessment summary'
    };
  }
};

export const getPersonalAssessmentHistory = async () => {
  try {
    const response = await api.get('/personal-assessment/history');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting personal assessment history:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get personal assessment history'
    };
  }
};

export const getSpecificPersonalAssessment = async (assessmentId) => {
  try {
    const response = await api.get(`/personal-assessment/${assessmentId}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting specific personal assessment:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get personal assessment'
    };
  }
};

export const downloadPersonalAssessmentPDF = async (businessId) => {
  try {
    const response = await api.get(`/pdf/personal-gap-assessment/${businessId}`, {
      responseType: 'blob'
    });

    // Create blob from response
    const blob = new Blob([response.data], { type: 'application/pdf' });

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];

    let filename = `personal_gap_assessment_${businessId}_${new Date().toISOString().split('T')[0]}.pdf`;
    if (contentDisposition) {
      // Try to extract filename from Content-Disposition header
      // Handles both: filename="file.pdf" and filename*=UTF-8''file.pdf
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(contentDisposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, '');
        // Remove UTF-8 encoding prefix if present
        filename = filename.replace(/^UTF-8''/, '');
        // Decode URI encoding if present
        try {
          filename = decodeURIComponent(filename);
        } catch (e) {
          // If decoding fails, use the filename as-is
          console.warn('[Personal Gap PDF] Failed to decode filename:', e);
        }
      }
    }


    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Error downloading personal assessment PDF:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to download PDF'
    };
  }
};

// Wealth Gap API Functions
export const getWealthGap = async () => {
  try {
    const response = await api.get('/wealth-gap');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting wealth gap:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get wealth gap'
    };
  }
};

export const saveWealthGap = async (wealthGapData) => {
  try {
    const response = await api.post('/wealth-gap', wealthGapData);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error saving wealth gap:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to save wealth gap'
    };
  }
};

export const calculateWealthGap = async (wealthGapData) => {
  try {
    const response = await api.post('/wealth-gap/calculate', wealthGapData);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error calculating wealth gap:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to calculate wealth gap'
    };
  }
};

// Value Gap API Functions
export const getValueGap = async () => {
  try {
    const response = await api.get('/value-gap');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting value gap:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get value gap'
    };
  }
};

export const autoCalculateValueGap = async () => {
  try {
    const response = await api.post('/value-gap/auto-calculate');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error auto-calculating value gap:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to auto-calculate value gap'
    };
  }
};

// Profit Gap API Functions
export const getProfitGap = async () => {
  try {
    const response = await api.get('/profit-gap');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting profit gap:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get profit gap'
    };
  }
};

export const autoCalculateProfitGap = async () => {
  try {
    const response = await api.post('/profit-gap/auto-calculate');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error auto-calculating profit gap:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to auto-calculate profit gap'
    };
  }
};

// Business Profile API Functions
export const getBusinessProfile = async () => {
  try {
    const response = await api.get('/business/profile');
    // Backend already returns {success: true, data: {profile: {...}}}, so just pass it through
    return response.data;
  } catch (error) {
    console.error('[API] Error getting business profile:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get business profile'
    };
  }
};

export const saveBusinessProfile = async (profileData) => {
  try {
    const response = await api.post('/business/profile', profileData);
    // Backend already returns {success: true, data: {profile: {...}}, message: '...'}, so just pass it through
    return response.data;
  } catch (error) {
    console.error('[API] Error saving business profile:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to save business profile'
    };
  }
};

// Exit Quiz API Functions
export const getExitQuizQuestions = async () => {
  try {
    const response = await api.get('/exit-quiz/questions');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting quiz questions:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get quiz questions'
    };
  }
};

export const submitExitQuiz = async (responses) => {
  try {
    const response = await api.post('/exit-quiz/submit', { responses });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error submitting quiz:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to submit quiz'
    };
  }
};

export const getExitQuizResults = async () => {
  try {
    const response = await api.get('/exit-quiz/results');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting quiz results:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get quiz results'
    };
  }
};

export const getExitQuizHistory = async () => {
  try {
    const response = await api.get('/exit-quiz/history');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting quiz history:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get quiz history'
    };
  }
};

// Valuation API Functions
export const getValuationHistory = async (type = null) => {
  try {
    const url = type ? `/valuation/history?type=${type}` : '/valuation/history';
    const response = await api.get(url);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting valuation history:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get valuation history'
    };
  }
};

export const getValuation = async (valuationId) => {
  try {
    const response = await api.get(`/valuation/${valuationId}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting valuation:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get valuation'
    };
  }
};

export const getIndustryData = async (industryId) => {
  try {
    const response = await api.get(`/valuation/industry/${industryId}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error getting industry data:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get industry data'
    };
  }
};

export const deleteValuation = async (valuationId) => {
  try {
    const response = await api.delete(`/valuation/${valuationId}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error deleting valuation:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to delete valuation'
    };
  }
};

export const getGoalSettings = async () => {
  try {
    const response = await api.get('/goal-settings/');
    return { success: true, data: response.data.data || [] };
  } catch (error) {
    console.error('Error getting goal settings:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get goal settings'
    };
  }
};

export const saveGoalSettings = async (settings) => {
  try {
    const response = await api.post('/goal-settings/', { settings });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error saving goal settings:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to save goal settings'
    };
  }
};

export default api;